import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouteNames } from "../../navigation/RouteNames";
import { useAppSelector } from "../../store/hooks";
import { useTranslation } from "../../translations/i18n";
import { formatLongDisplayDate } from "../../utils/helperFunctions/dateFormatter";
import { canManageBilling } from "../../utils/helperFunctions/roleHelper";
import {
  getSubscriptionAccessState,
  getSubscriptionExpiryValue,
  isCustomSubscription,
} from "../../utils/helperFunctions/subscriptionAccessHelper";

const SubscriptionAccessModal = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { account, user } = useAppSelector((state) => state.auth);

  const [dismissedNoticeKey, setDismissedNoticeKey] = useState<string | null>(
    null,
  );

  const accessState = getSubscriptionAccessState(account);

  const customSubscription = isCustomSubscription(account);

  const canTakeBillingAction = canManageBilling(user?.role);

  const expiryValue = getSubscriptionExpiryValue(account);

  const formattedExpiryDate = expiryValue
    ? formatLongDisplayDate(expiryValue)
    : "-";

  const noticeKey = useMemo(() => {
    if (!account || accessState === "active") {
      return null;
    }

    return [
      account.id,
      accessState,
      account.subscription_status,
      account.subscription_valid_until || "no-expiry",
    ].join(":");
  }, [account, accessState]);

  useEffect(() => {
    setDismissedNoticeKey(null);
  }, [noticeKey]);

  if (!noticeKey || accessState === "active") {
    return null;
  }

  const open = dismissedNoticeKey !== noticeKey;

  const title =
    accessState === "expired"
      ? t("subscription.accessExpiredTitle")
      : t("subscription.accessWarningTitle");

  const message =
    accessState === "expired"
      ? t(
          customSubscription
            ? "subscription.customAccessExpiredMessage"
            : "subscription.accessExpiredMessage",
        )
      : t(
          customSubscription
            ? "subscription.customAccessWarningMessage"
            : "subscription.accessWarningMessage",
          {
            date: formattedExpiryDate,
          },
        );

  const closeModal = () => {
    setDismissedNoticeKey(noticeKey);
  };

  const handleAction = () => {
    closeModal();

    navigate(customSubscription ? RouteNames.support : RouteNames.subscription);
  };

  return (
    <Dialog open={open} onClose={closeModal} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          color: "error.main",
          fontWeight: 950,
          fontSize: 24,
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={{
            pt: 1,
            fontSize: 17,
            fontWeight: 700,
            lineHeight: 1.6,
          }}
        >
          {message}
        </Typography>

        {!canTakeBillingAction ? (
          <Typography
            sx={{
              mt: 1.5,
              fontWeight: 850,
            }}
          >
            {t("subscription.contactAccountOwnerMessage")}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
        }}
      >
        <Button onClick={closeModal} variant="outlined">
          {t("subscription.noticeCloseCta")}
        </Button>

        {canTakeBillingAction ? (
          <Button onClick={handleAction} variant="contained" color="error">
            {t(
              customSubscription
                ? "subscription.contactSupportCta"
                : "subscription.viewPlansCta",
            )}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionAccessModal;
